import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowRight, ShieldCheck, Trees, Users, Handshake, Download, FileText,
    CheckCircle2, Send, Loader2, Lock, Building, Coins, Banknote, Network,
    Calendar, Eye, BadgeCheck, Globe2, Share2, Copy as CopyIcon, MessageCircle, Linkedin,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, formatApiError, API } from "@/lib/api";
import { TLTB_LANGS, tltbT } from "@/lib/tltbStrings";

const INTEREST_OPTIONS = [
    { id: "tltb_partnership_meeting", label: "Schedule a partnership meeting" },
    { id: "mataqali_introduction", label: "Mataqali landowner unit introduction" },
    { id: "joint_venture_briefing", label: "Joint Venture model briefing" },
    { id: "regulatory_alignment", label: "Regulatory alignment discussion" },
    { id: "other", label: "Something else" },
];

const HANDLE_ICONS = [ShieldCheck, Banknote, Building, Network, ShieldCheck, Coins, Users, BadgeCheck];
const GOV_ICONS = [Trees, Banknote, ShieldCheck, Eye];

export default function TLTBPartnership() {
    const [contactOpen, setContactOpen] = useState(false);
    const [lang, setLang] = useState("en");
    const t = tltbT(lang);

    const downloadPdf = (langOverride) => {
        const useLang = langOverride || lang || "en";
        const link = document.createElement("a");
        link.href = `${API}/tltb/pack.pdf?lang=${useLang}`;
        link.download = useLang === "en"
            ? "bula-tltb-partnership-pack.pdf"
            : `bula-tltb-partnership-pack-${useLang}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        const labelMap = { en: "English", fj: "Vakaviti", hi: "हिन्दी" };
        toast.success(`Downloading partnership pack — ${labelMap[useLang] || useLang}…`);
    };

    const buildShareMessage = () => {
        const url = typeof window !== "undefined"
            ? `${window.location.origin}/tltb-partnership`
            : "https://bularealestatefiji.com/tltb-partnership";
        return `${t.shareMessage} ${url}`;
    };

    const copyToClipboard = async (text) => {
        // Modern path
        if (navigator.clipboard?.writeText) {
            try {
                await navigator.clipboard.writeText(text);
                return true;
            } catch {
                /* fall through */
            }
        }
        // Legacy fallback for environments without clipboard permission (Safari iframe, etc.)
        try {
            const ta = document.createElement("textarea");
            ta.value = text;
            ta.style.position = "fixed";
            ta.style.opacity = "0";
            document.body.appendChild(ta);
            ta.select();
            const ok = document.execCommand("copy");
            document.body.removeChild(ta);
            return ok;
        } catch {
            return false;
        }
    };

    const trackShare = async (method) => {
        // Fire-and-forget analytics — never blocks the user's share action
        try {
            await api.post("/tltb/share-track", { method, lang });
        } catch {
            /* best-effort */
        }
    };

    const shareCopy = async () => {
        const text = buildShareMessage();
        const ok = await copyToClipboard(text);
        if (ok) {
            toast.success(t.shareCopiedToast);
        } else {
            // Last-resort: open a prompt so the user can hand-copy.
            window.prompt(t.shareCopy, text);
        }
        trackShare("copy");
    };

    const shareWhatsapp = () => {
        const text = encodeURIComponent(buildShareMessage());
        window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
        trackShare("whatsapp");
    };

    const shareLinkedin = async () => {
        const url = typeof window !== "undefined"
            ? `${window.location.origin}/tltb-partnership`
            : "https://bularealestatefiji.com/tltb-partnership";
        // LinkedIn's share URL only accepts the link (their crawler reads OG tags); we copy the
        // localised body to clipboard so the user can paste it as the post commentary.
        const copied = await copyToClipboard(buildShareMessage());
        if (copied) toast.success(t.shareCopiedToast);
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, "_blank", "noopener,noreferrer");
        trackShare("linkedin");
    };

    return (
        <div data-testid="tltb-partnership-page" className="bg-background min-h-screen">
            {/* HERO */}
            <section className="relative overflow-hidden bg-ocean-deep-grad text-white py-24 lg:py-32">
                <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-coral/10 blur-3xl pointer-events-none" />
                <div className="absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-fiji-green/10 blur-3xl pointer-events-none" />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-coral/15 border border-coral/30 mb-6">
                        <Handshake className="h-3.5 w-3.5 text-coral" />
                        <span className="text-[10px] uppercase tracking-[0.25em] font-app font-bold text-coral">
                            {t.heroBadge}
                        </span>
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.05] mb-6 max-w-5xl">
                        {t.heroTitlePre}<span className="italic text-turquoise">{t.heroTitleAccent}</span>.
                    </h1>
                    <p className="font-app text-base sm:text-lg lg:text-xl text-white/85 leading-relaxed max-w-3xl mb-10">
                        {t.heroSubtitle}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center flex-wrap">
                        <Button
                            size="lg"
                            data-testid="tltb-hero-download-btn"
                            onClick={() => downloadPdf()}
                            className="bg-coral hover:bg-coral-deep text-white font-app"
                        >
                            <Download className="h-4 w-4 mr-2" />
                            {t.btnDownloadPdf}
                        </Button>
                        <Button
                            size="lg"
                            variant="outline"
                            data-testid="tltb-hero-meeting-btn"
                            onClick={() => setContactOpen(true)}
                            className="bg-white/5 border-white/20 text-white hover:bg-white/15 hover:text-white font-app"
                        >
                            {t.btnScheduleMeeting} <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>

                        {/* Language picker */}
                        <div
                            data-testid="tltb-pdf-lang-picker"
                            className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/5 border border-white/15"
                        >
                            <Globe2 className="h-3.5 w-3.5 text-turquoise" />
                            <label htmlFor="tltb-lang-select" className="text-[10px] uppercase tracking-wider font-app text-white/70 mr-1">
                                {t.pdfLanguageLabel}
                            </label>
                            <select
                                id="tltb-lang-select"
                                value={lang}
                                onChange={(e) => setLang(e.target.value)}
                                data-testid="tltb-pdf-lang-select"
                                className="bg-transparent text-white text-sm font-app focus:outline-none cursor-pointer"
                                aria-label={t.pdfLanguageA11y}
                            >
                                {TLTB_LANGS.map((l) => (
                                    <option key={l.code} value={l.code} className="bg-ocean-deep text-white">
                                        {l.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Share dropdown */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    size="lg"
                                    variant="outline"
                                    data-testid="tltb-share-btn"
                                    className="bg-white/5 border-white/20 text-white hover:bg-white/15 hover:text-white font-app"
                                >
                                    <Share2 className="h-4 w-4 mr-2" />
                                    {t.btnShare}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-60">
                                <DropdownMenuItem onClick={shareWhatsapp} data-testid="tltb-share-whatsapp" className="font-app">
                                    <MessageCircle className="h-4 w-4 mr-2 text-fiji-green" />
                                    {t.shareWhatsapp}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={shareLinkedin} data-testid="tltb-share-linkedin" className="font-app">
                                    <Linkedin className="h-4 w-4 mr-2 text-ocean-deep dark:text-turquoise" />
                                    {t.shareLinkedin}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={shareCopy} data-testid="tltb-share-copy" className="font-app">
                                    <CopyIcon className="h-4 w-4 mr-2 text-coral" />
                                    {t.shareCopy}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-12">
                        {[
                            { num: "83%", label: t.stat1Lbl },
                            { num: "+138%", label: t.stat2Lbl },
                            { num: "12", label: t.stat3Lbl },
                            { num: "0", label: t.stat4Lbl },
                        ].map((s) => (
                            <div key={s.label} className="bg-white/5 border border-white/10 rounded-lg p-4">
                                <div className="font-display text-3xl font-bold text-turquoise tabular-nums">{s.num}</div>
                                <div className="text-xs uppercase tracking-wider text-white/70 font-app mt-1 leading-tight">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* OPPORTUNITY */}
            <section className="py-20 lg:py-24">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-[10px] uppercase tracking-[0.3em] font-app font-bold text-coral mb-3">
                        {t.oppKicker}
                    </div>
                    <h2 className="font-display text-3xl lg:text-4xl font-bold mb-6 text-foreground leading-tight">
                        {t.oppTitlePre}<span className="italic text-coral">{t.oppTitleAccent}</span>
                    </h2>
                    <div className="prose prose-slate dark:prose-invert font-app text-base text-muted-foreground leading-relaxed max-w-3xl">
                        <p>{t.oppP1}</p>
                        <p>{t.oppP2}</p>
                    </div>
                    <ul className="space-y-2 mt-5 max-w-3xl">
                        {t.oppBullets.map((s) => (
                            <li key={s} className="flex items-start gap-2.5 font-app text-sm text-foreground/85">
                                <CheckCircle2 className="h-4 w-4 text-fiji-green mt-0.5 shrink-0" />
                                <span>{s}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* JV MODEL */}
            <section className="py-20 lg:py-24 bg-secondary/40 dark:bg-card/30 border-y border-border">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-[10px] uppercase tracking-[0.3em] font-app font-bold text-coral mb-3">
                        {t.jvKicker}
                    </div>
                    <h2 className="font-display text-3xl lg:text-4xl font-bold mb-4 text-foreground leading-tight">
                        {t.jvTitlePre}<span className="italic text-fiji-green">{t.jvTitleAccent}</span>
                    </h2>
                    <p className="font-app text-base text-muted-foreground leading-relaxed max-w-3xl mb-10">
                        {t.jvIntro}
                    </p>

                    <Card className="overflow-hidden bg-card" data-testid="tltb-jv-table">
                        <div className="grid grid-cols-3 bg-ocean-deep-grad text-white">
                            <div className="p-4 font-app text-xs uppercase tracking-wider font-bold">{t.jvColElement}</div>
                            <div className="p-4 font-app text-xs uppercase tracking-wider font-bold border-l border-white/10">{t.jvColMataqali}</div>
                            <div className="p-4 font-app text-xs uppercase tracking-wider font-bold border-l border-white/10">{t.jvColCapital}</div>
                        </div>
                        {[
                            [t.jvRowEquityL, t.jvRowEquityM, t.jvRowEquityC],
                            [t.jvRowCashL, t.jvRowCashM, t.jvRowCashC],
                            [t.jvRowIncomeL, t.jvRowIncomeM, t.jvRowIncomeC],
                            [t.jvRowDecisionL, t.jvRowDecisionM, t.jvRowDecisionC],
                            [t.jvRowExitL, t.jvRowExitM, t.jvRowExitC],
                        ].map((row, i) => (
                            <div key={row[0]} className={`grid grid-cols-3 ${i % 2 === 0 ? "bg-card" : "bg-secondary/40 dark:bg-card/60"} border-b border-border last:border-0`}>
                                <div className="p-4 font-app text-sm font-semibold text-foreground">{row[0]}</div>
                                <div className="p-4 font-app text-sm text-foreground/85 border-l border-border">{row[1]}</div>
                                <div className="p-4 font-app text-sm text-foreground/85 border-l border-border">{row[2]}</div>
                            </div>
                        ))}
                    </Card>
                </div>
            </section>

            {/* WHAT WE HANDLE */}
            <section className="py-20 lg:py-24">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-[10px] uppercase tracking-[0.3em] font-app font-bold text-coral mb-3">
                        {t.wwhKicker}
                    </div>
                    <h2 className="font-display text-3xl lg:text-4xl font-bold mb-4 text-foreground leading-tight">
                        {t.wwhTitlePre}<span className="italic text-coral">{t.wwhTitleAccent}</span>
                    </h2>
                    <p className="font-app text-base text-muted-foreground leading-relaxed max-w-3xl mb-10">
                        {t.wwhIntro}
                    </p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {t.wwhBlocks.map((b, i) => {
                            const Icon = HANDLE_ICONS[i] || ShieldCheck;
                            return (
                                <Card key={b.label} data-testid={`tltb-handle-${i}`} className="p-4 bg-card hover:border-coral/40 transition">
                                    <div className="h-9 w-9 rounded-md bg-coral/10 grid place-items-center mb-3">
                                        <Icon className="h-4 w-4 text-coral" />
                                    </div>
                                    <div className="font-display text-sm font-bold text-foreground mb-1.5 leading-tight">{b.label}</div>
                                    <p className="font-app text-xs text-muted-foreground leading-relaxed">{b.desc}</p>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* GOVERNANCE */}
            <section className="py-20 lg:py-24 bg-secondary/40 dark:bg-card/30 border-y border-border">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-[10px] uppercase tracking-[0.3em] font-app font-bold text-coral mb-3">
                        {t.govKicker}
                    </div>
                    <h2 className="font-display text-3xl lg:text-4xl font-bold mb-10 text-foreground leading-tight">
                        {t.govTitle}
                    </h2>

                    <Card className="p-6 lg:p-7 mb-8 bg-coral/5 dark:bg-coral/10 border-coral/30" data-testid="tltb-platform-callout">
                        <div className="flex items-start gap-4">
                            <div className="h-12 w-12 rounded-md bg-coral/15 grid place-items-center shrink-0">
                                <Lock className="h-5 w-5 text-coral" />
                            </div>
                            <div>
                                <div className="font-display text-lg font-bold text-foreground mb-2">{t.govCalloutTitle}</div>
                                <p className="font-app text-sm text-foreground/85 leading-relaxed">
                                    {t.govCalloutBody}
                                </p>
                            </div>
                        </div>
                    </Card>

                    <div className="grid sm:grid-cols-2 gap-3">
                        {t.govItems.map((g, i) => {
                            const Icon = GOV_ICONS[i] || ShieldCheck;
                            return (
                                <Card key={g.title} className="p-5 bg-card flex items-start gap-3">
                                    <Icon className="h-5 w-5 text-fiji-green mt-0.5 shrink-0" />
                                    <div>
                                        <div className="font-display text-sm font-bold text-foreground mb-1">{g.title}</div>
                                        <p className="font-app text-xs text-muted-foreground leading-relaxed">{g.body}</p>
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* HOW WE PROPOSE TO ENGAGE */}
            <section className="py-20 lg:py-24">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-[10px] uppercase tracking-[0.3em] font-app font-bold text-coral mb-3">
                        {t.engageKicker}
                    </div>
                    <h2 className="font-display text-3xl lg:text-4xl font-bold mb-10 text-foreground leading-tight">
                        {t.engageTitlePre}<span className="italic text-coral">{t.engageTitleAccent}</span>
                    </h2>
                    <div className="space-y-3">
                        {t.engageSteps.map((step) => (
                            <Card key={step.num} className="p-5 bg-card hover:border-coral/40 transition flex items-start gap-5">
                                <div className="font-display text-3xl font-bold text-coral tabular-nums shrink-0 w-12">{step.num}</div>
                                <div>
                                    <div className="font-display text-base font-bold text-foreground mb-1">{step.title}</div>
                                    <p className="font-app text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* TRACK RECORD strip */}
            <section className="py-12 bg-fiji-green/5 dark:bg-fiji-green/10 border-y border-fiji-green/30">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <p className="font-app text-base text-foreground/85 leading-relaxed mb-3">
                        {t.trackBody} <strong className="text-fiji-green">{t.trackBodyAccent}</strong>{t.trackBodySuffix}
                    </p>
                    <Button asChild variant="outline" data-testid="tltb-hall-of-fame-cta" className="font-app">
                        <Link to="/hall-of-fame">{t.trackCta} <ArrowRight className="h-3.5 w-3.5 ml-2" /></Link>
                    </Button>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="py-20 lg:py-24 bg-ocean-deep-grad text-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-coral/15 border border-coral/30 mb-6">
                        <Calendar className="h-3.5 w-3.5 text-coral" />
                        <span className="text-[10px] uppercase tracking-[0.25em] font-app font-bold text-coral">
                            {t.finalKicker}
                        </span>
                    </div>
                    <h2 className="font-display text-3xl lg:text-5xl font-bold mb-5 leading-tight">
                        {t.finalTitlePre}<span className="italic text-turquoise">{t.finalTitleAccent}</span>
                    </h2>
                    <p className="font-app text-base lg:text-lg text-white/85 leading-relaxed max-w-2xl mx-auto mb-8">
                        {t.finalP}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button
                            size="lg"
                            data-testid="tltb-final-meeting-btn"
                            onClick={() => setContactOpen(true)}
                            className="bg-coral hover:bg-coral-deep text-white font-app"
                        >
                            <Send className="h-4 w-4 mr-2" />
                            {t.btnRequestMeeting}
                        </Button>
                        <Button
                            size="lg"
                            variant="outline"
                            data-testid="tltb-final-download-btn"
                            onClick={() => downloadPdf()}
                            className="bg-white/5 border-white/20 text-white hover:bg-white/15 hover:text-white font-app"
                        >
                            <FileText className="h-4 w-4 mr-2" />
                            {t.btnDownloadPdf}
                        </Button>
                    </div>
                </div>
            </section>

            {contactOpen && (
                <ContactDialog onClose={() => setContactOpen(false)} />
            )}
        </div>
    );
}

function ContactDialog({ onClose }) {
    const [form, setForm] = useState({
        full_name: "",
        organisation: "",
        role: "",
        email: "",
        phone: "",
        interest: "tltb_partnership_meeting",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.full_name || !form.organisation || !form.email || (form.message || "").length < 10) {
            toast.error("Name, organisation, email, and a message of 10+ characters are required");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/tltb/contact", form);
            setDone(true);
            toast.success("Request sent — we'll reply within one business day.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not send request");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="tltb-contact-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display text-foreground">TLTB Partnership Enquiry</DialogTitle>
                    <DialogDescription className="font-app text-muted-foreground">
                        Confidential. We'll reply within one business day to schedule a briefing.
                    </DialogDescription>
                </DialogHeader>

                {done ? (
                    <div className="py-8 text-center" data-testid="tltb-contact-done">
                        <CheckCircle2 className="h-12 w-12 text-fiji-green mx-auto mb-3" />
                        <div className="font-display text-xl font-bold text-foreground mb-2">Request received</div>
                        <p className="font-app text-sm text-muted-foreground">
                            Reply incoming to <strong className="text-foreground">{form.email}</strong>.
                        </p>
                        <Button onClick={onClose} className="mt-5 bg-ocean-green text-white">Close</Button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-3" data-testid="tltb-contact-form">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Full name *</Label>
                                <Input data-testid="tltb-contact-name" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} required className="bg-card text-foreground" />
                            </div>
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Email *</Label>
                                <Input data-testid="tltb-contact-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required className="bg-card text-foreground" />
                            </div>
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Organisation *</Label>
                                <Input data-testid="tltb-contact-org" value={form.organisation} onChange={(e) => update("organisation", e.target.value)} required className="bg-card text-foreground" />
                            </div>
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Role / Title</Label>
                                <Input data-testid="tltb-contact-role" value={form.role} onChange={(e) => update("role", e.target.value)} className="bg-card text-foreground" />
                            </div>
                            <div className="col-span-2">
                                <Label className="text-xs font-app text-muted-foreground">Phone</Label>
                                <Input data-testid="tltb-contact-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="bg-card text-foreground" />
                            </div>
                            <div className="col-span-2">
                                <Label className="text-xs font-app text-muted-foreground">Interest *</Label>
                                <Select value={form.interest} onValueChange={(v) => update("interest", v)}>
                                    <SelectTrigger data-testid="tltb-contact-interest" className="bg-card text-foreground"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {INTEREST_OPTIONS.map((o) => (
                                            <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs font-app text-muted-foreground">Message *</Label>
                            <Textarea data-testid="tltb-contact-message" value={form.message} onChange={(e) => update("message", e.target.value)} rows={4} required minLength={10} placeholder="Background on your interest, who would be in the meeting, available dates…" className="bg-card text-foreground resize-none" />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="ghost" onClick={onClose} className="font-app">Cancel</Button>
                            <Button type="submit" disabled={submitting} data-testid="tltb-contact-submit" className="bg-coral hover:bg-coral-deep text-white">
                                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                                {submitting ? "Sending…" : "Send enquiry"}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
